import styled from 'styled-components';
import css from '@styled-system/css';
import { BLOCKS } from '@contentful/rich-text-types';

import { theme } from 'utils/theme.v2';
import { featuredValuePropProps } from 'humanity/sections/featuredValueProp/featuredValueProp.types';
import Section from 'humanity/primitives/section';
import Container from 'humanity/primitives/container';
import Box from 'humanity/primitives/box';
import Grid from 'humanity/primitives/grid';
import Flex from 'humanity/primitives/flex';
import Heading from 'humanity/primitives/heading';
import Text from 'humanity/primitives/text';
import Image from 'humanity/primitives/image';
import SimpleRichText from 'humanity/components/simpleRichText';
import Cta from 'humanity/components/cta';
import CheckCircle from 'humanity/icons/checkCircle';
import { getPaddingValues } from 'utils/getPaddingValues';
import Disclosures from 'humanity/components/disclosures';

const alignment = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
};

const ImageWrapper = styled(Flex)(
  ({ hasDisclosures, bottomPadding, imagePosition, imageAlignment }) =>
    css({
      gridColumnStart: imagePosition === 'left' ? '1' : ['1', null, null, '7'],
      gridColumnEnd: ['span 12', null, null, 'span 6'],
      gridRow: ['2', null, null, '1'],
      alignSelf: 'stretch',
      alignItems: alignment[imageAlignment],
      justifyContent: 'center',
      mt: [5, null, null, 0],
      pb:
        imageAlignment === 'bottom' || hasDisclosures
          ? 0
          : getPaddingValues(bottomPadding),
    })
);

const CopyWrapper = styled(Box)(({ hasDisclosures, imagePosition, bottomPadding }) =>
  css({
    gridColumnStart: imagePosition === 'left' ? ['1', null, null, '8'] : '1',
    gridColumnEnd: ['span 12', null, null, 'span 5'],
    gridRow: '1',
    alignSelf: 'start',
    pb: [0, null, null, hasDisclosures ? 0 : bottomPadding],
  })
);

const StyledDisclosures = styled(Disclosures)(({ bottomPadding }) =>
  css({
    fontSize: 'small',
    gridColumn: '1 / -1',
    gridRow: ['3', null, null, '2'],
    paddingBottom: getPaddingValues(bottomPadding),
  })
);

const iconHeight = '26px';

const IconWrapper = styled(Flex)({
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
  alignSelf: 'flex-start',
});

const StyledListItem = styled.li(
  css({
    display: 'flex',
    alignItems: 'center',
    ml: 0,
    mt: 4,
    listStyleType: 'none',
    '&:first-child': {
      mt: 3,
    },
    '& p': {
      mt: 0,
      ml: 1,
      fontSize: 2,
      lineHeight: iconHeight,
    },
    '& b, & strong': {
      fontWeight: 600,
      color: 'blue100',
    },
  })
);

const renderOpts = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Text variant="lead" color="blue100">
        {children}
      </Text>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => (
      <StyledListItem>
        <IconWrapper>
          <CheckCircle size={26} color={theme.colors.teal100} />
        </IconWrapper>
        {children}
      </StyledListItem>
    ),
  },
};

const FeaturedValueProp = ({
  backgroundColor = 'white',
  topPadding = 128,
  bottomPadding = 128,
  image,
  imagePosition,
  imageAlignment,
  eyebrow = '',
  heading,
  paragraph,
  disclosures,
  cta = null,
}) => (
  <Section
    data-testid="FeaturedValueProp"
    pt={getPaddingValues(topPadding)}
    bg={backgroundColor}
  >
    <Container>
      <Grid alignItems="center">
        <ImageWrapper
          data-testid="FeaturedValuePropImage"
          imagePosition={imagePosition}
          imageAlignment={imageAlignment}
          bottomPadding={bottomPadding}
          hasDisclosures={!!disclosures}
        >
          <Image
            src={image?.url}
            width={584}
            height={584}
            imgixParams={{ fit: 'crop', ar: '1:1' }}
            quality={100}
          />
        </ImageWrapper>
        <CopyWrapper
          data-testid="FeaturedValuePropCopy"
          imagePosition={imagePosition}
          bottomPadding={bottomPadding}
          hasDisclosures={!!disclosures}
        >
          {!!eyebrow?.length && (
            <Text variant="eyebrow" fontWeight="bold" color="teal100">
              {eyebrow}
            </Text>
          )}
          <Heading
            variant="h3"
            mt={2}
            dangerouslySetInnerHTML={{ __html: heading?.trim() }}
          />
          <Box mt={5}>
            <SimpleRichText content={paragraph?.json} renderOptions={renderOpts} />
          </Box>
          {cta && <Cta mt={5} location="featured_value_prop" btnSize="lg" {...cta} />}
        </CopyWrapper>
        {disclosures && (
          <StyledDisclosures bottomPadding={bottomPadding} disclosures={disclosures} />
        )}
      </Grid>
    </Container>
  </Section>
);

FeaturedValueProp.propTypes = {
  ...featuredValuePropProps,
};

export default FeaturedValueProp;
